import {captureException} from '@sentry/browser';
import {Spec} from 'immutability-helper';
import React from 'react';

import {assert, assertAndContinue} from 'Asserts';
import {StoichiometryGroupView} from 'notebook/integrations/chemistry/StoichiometryGroupView';
import type {StoichiometryGroup} from 'notebook/integrations/chemistry/StoichiometryNoteLinkedGroupAdapter';
import MediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import {MediocreItemAction} from 'notebook/mediocre/EditorTypes';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {MediocreSearchResult, TextMatcher} from 'notebook/mediocre/search/SearchResults';
import {
  getNoteLinkedGroupAdapters,
  isKnownNoteLinkedGroupAdapter,
} from 'notebook/notes/groups/NoteLinkedGroupAdapters';
import NoteConstants, {CidIdMap, ContainedServerIdsFromClipboard} from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorPluginWithAdapter, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {FIELDS_PER_ROW} from 'notebook/structured-tables/StructuredTableForm';
import {STRUCTURED_TABLE_PLUGIN} from 'notebook/structured-tables/StructuredTablePlugin';
import {doesUserHavePermissionToViewTable} from 'notebook/structured-tables/util/doesUserHavePermissionToViewTable';
import {CollapsedTableState} from 'notebook/tables/collapse/reduceCollapsedTablesUI';
import {TABLE_SEARCH_PATHS} from 'notebook/tables/constants/TableConstants';
import {TABLE_HEADER_HEIGHT} from 'notebook/tables/constants/TableLayoutConstants';
import {getTableSkeletonHeight} from 'notebook/tables/core-ui/TableSkeleton';
import {OutputTableMap} from 'notebook/tables/excess/EvaluationConstants';
import {isNoteLinkedFile} from 'notebook/TypePredicates';
import {
  isNotePartWithItem,
  NoteLinkedObject,
  NoteLinkedObjectType,
  NotePart,
  NotePartWithItem,
} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';

import {generateNoteLinkedObjectCollapseId} from '../util/generateNLCollapseId';
import getNoteLinkedObjectsOfTypes from '../util/getNoteLinkedObjectsOfTypes';
import NotePartGrid from './note-part-grid/NotePartGrid';
import {
  isImageNotePartGrid,
  NotePartGrid as NotePartGridType,
} from './note-part-grid/NotePartGridGroupConstants';
import {computeNotePartGridVirtualizedHeight} from './note-part-grid/util/computeVirtualizedHeight';
import {
  NoteLinkedGroup,
  NoteLinkedGroupItemProps,
  NoteLinkedGroupKind,
  stashContainedServerIdsFromClipboard,
  UnknownNoteLinkedGroup,
  UnknownNoteLinkedGroupAdapter,
} from './NoteLinkedGroupConstants';
import {NoteLinkedGroupView} from './NoteLinkedGroupView';
import {isStructuredTableNotePart} from './util/assertIsStructuredTableNotePart';
import {getMetadataForRestoreForGroup} from './util/NoteLinkedGroupSelection';

const FORM_ROW_APPROX_HEIGHT = 90; // label + input

// Whether a group may be pasted into a DIFFERENT entry/template/document than it was copied from.
// Loosens only the ENTRY boundary; the cross-TENANT guard (Editor.itemFromHTML) is separate and
// always enforced. Image grids are special-cased here rather than opting in through their adapter:
// only all-image NOTE_PART_GRIDs qualify, and the adapter's item-less supportsCrossEntryPaste() can't
// inspect the group's contents to make that distinction. Every other kind opts in via its adapter.
// TODO(BNCH-14855): fold the image-grid case into the NotePartGrid adapter once supportsCrossEntryPaste
// can see the group (e.g. by passing it the group, or via a dedicated ImageNotePartGrid adapter).
function supportsCrossEntryPaste(group: UnknownNoteLinkedGroup): boolean {
  return isImageNotePartGrid(group) || getNoteLinkedGroupAdapters()[group.kind].supportsCrossEntryPaste();
}

// Keyed on isNoteLinkedFile so it covers both an image grid's image files and a reaction group's
// (optional) scheme file. Files whose server id `resolveServerId` cannot resolve are omitted, so the
// map may be empty.
function collectContainedServerIds(
  group: UnknownNoteLinkedGroup,
  resolveServerId: (containedFileCid: string) => string | number | undefined
): ContainedServerIdsFromClipboard {
  const containedServerIds: ContainedServerIdsFromClipboard = {};
  for (const containedNotePart of group.containedNoteParts) {
    if (isNotePartWithItem(containedNotePart) && isNoteLinkedFile(containedNotePart.item)) {
      const serverId = resolveServerId(containedNotePart.item.cid);
      if (serverId != null) {
        containedServerIds[containedNotePart.item.cid] = serverId;
      }
    }
  }
  return containedServerIds;
}

export const NOTE_LINKED_GROUP_PLUGIN: NoteEditorPluginWithAdapter<UnknownNoteLinkedGroupAdapter> = {
  type: NoteLinkedObjectType.NOTE_LINKED_GROUP,
  typeClass: 'is-group',
  title: 'Group',
  icon: 'notdef',

  get: (kind: NoteLinkedGroupKind): UnknownNoteLinkedGroupAdapter => {
    const adapter = getNoteLinkedGroupAdapters()[kind];
    assert(adapter != null, `Unknown note linked group kind: ${kind}`);
    return adapter;
  },

  // All of NotePartGrids, Stoichiometry groups, & Mixture Prep tables are collapsible and should be
  // caught here.
  isCollapsible: () => true,
  canAutoCollapse: false,

  component: {
    ...MediocrePlugins.selectablePluginBase,
    required: [],

    getVirtualizedHeight(item: NotePart & {item: NoteLinkedGroup<unknown>}) {
      const group = item.item;

      if (group.kind === NoteLinkedGroupKind.NOTE_PART_GRID) {
        return computeNotePartGridVirtualizedHeight(group as NotePartGridType);
      } else if (group.kind === NoteLinkedGroupKind.STOICHIOMETRY_GROUP) {
        return 0; // Sketcher + table height is dynamic; skip virtualization
      } else if (group.kind === NoteLinkedGroupKind.MIXTURE_GROUP) {
        let height = TABLE_HEADER_HEIGHT;
        const noteLinkedGroup = item.item;
        noteLinkedGroup.containedNoteParts.forEach((notePart) => {
          if (!isStructuredTableNotePart(notePart)) {
            assertAndContinue(false, 'Only structured tables supported in NoteLinkedGroup');
            return;
          }
          const adapter = getStructuredTableAdapters()[notePart.item.kind];
          const showAsForm = adapter.shouldShowAsForm(notePart.item);
          if (!showAsForm) {
            // NoteLinkedGroupView explicitly sets isCollapsible: false
            height += getTableSkeletonHeight(notePart.item.rows.length, false);
          } else {
            // Compute form height for form
            const formRows = Math.ceil(notePart.item.columns.length / FIELDS_PER_ROW);
            height += formRows * FORM_ROW_APPROX_HEIGHT;
          }
        });
        return height;
      }
      return 0;
    },

    getVirtualizationId(this: NoteEditor, notePart: NotePart & {item: NoteLinkedGroup<unknown>}): string {
      return notePart.item.provenanceUuid;
    },

    // Groups track their collapsed state in the shared collapsedTables redux slice, keyed by the
    // group's provenanceUuid (see NoteLinkedGroupView). Exposing it here lets the Ctrl-F search
    // pipeline learn whether the group is collapsed so it can skip contained tables that are not
    // rendered (and therefore have no target to scroll to / highlight).
    getIsCollapsed(
      item: NotePart & {item: NoteLinkedGroup<unknown>},
      _editorProps: NoteEditorProps,
      collapsedStateById: CollapsedTableState
    ): boolean {
      const collapseId = generateNoteLinkedObjectCollapseId(_editorProps.fileId, item.item.provenanceUuid);
      return collapsedStateById[collapseId] ?? false;
    },

    make(
      this: NoteEditor,
      notePart: NotePart & {item: NoteLinkedGroup<unknown>},
      index: number,
      editorProps: NoteEditorProps,
      itemProps: NoteLinkedGroupItemProps,
      searchResultProps?: MediocreSearchResultProps
    ): JSX.Element {
      const {item} = notePart;
      const {kind} = item;
      if (!isKnownNoteLinkedGroupAdapter(kind)) {
        throw new Error(`Unknown note linked group kind: ${kind}`);
      }
      if (kind === NoteLinkedGroupKind.NOTE_PART_GRID) {
        return (
          <NotePartGrid
            notePartCid={notePart.cid}
            group={item as NotePartGridType}
            editor={this}
            editorProps={editorProps}
            itemProps={itemProps}
          />
        );
      }
      if (kind === NoteLinkedGroupKind.STOICHIOMETRY_GROUP) {
        return (
          <StoichiometryGroupView
            notePartCid={notePart.cid}
            group={item as StoichiometryGroup}
            editor={this}
            editorProps={editorProps}
            spreadsheetProps={itemProps?.spreadsheetProps ?? {metadataForRestore: null}}
            {...searchResultProps}
          />
        );
      }
      return (
        <NoteLinkedGroupView
          notePartCid={notePart.cid}
          group={item}
          editor={this}
          editorProps={editorProps}
          {...itemProps}
          {...searchResultProps}
        />
      );
    },

    getItemProps(
      this: NoteEditor,
      notePart: NotePartWithItem<UnknownNoteLinkedGroup>
    ): NoteLinkedGroupItemProps {
      const resolvedContainedItemIds =
        notePart.item.kind === NoteLinkedGroupKind.NOTE_PART_GRID
          ? notePart.item.containedNoteParts?.map((p) => {
              const cid = (p.item as NoteLinkedObject | null)?.cid;
              return cid ? this.props.getIdFromCid?.(cid) : undefined;
            })
          : undefined;

      // Include computed tables for the NLG's contained tables so that
      // MediocreItem's deep-equal check on itemProps detects when
      // evaluated values change (e.g. due to cross-group cell references).
      // Only relevant for groups that opt in to entry-level evaluation.
      let containedComputedTables: OutputTableMap | undefined;
      const adapter = isKnownNoteLinkedGroupAdapter(notePart.item.kind)
        ? getNoteLinkedGroupAdapters()[notePart.item.kind]
        : undefined;
      if (this.props.computedTables && adapter?.supportsEntryLevelEvaluation()) {
        containedComputedTables = {};
        for (const containedNotePart of notePart.item.containedNoteParts) {
          if (isStructuredTableNotePart(containedNotePart)) {
            const tableAdapter = getStructuredTableAdapters()[containedNotePart.item.kind];
            const tableName = tableAdapter.toTable(containedNotePart.item).name;
            if (this.props.computedTables[tableName]) {
              containedComputedTables[tableName] = this.props.computedTables[tableName];
            }
          }
        }
      }

      return {
        spreadsheetProps: {
          metadataForRestore: getMetadataForRestoreForGroup(notePart.item, this.props.metadataForRestore),
        },
        resolvedContainedItemIds,
        containedComputedTables,
        ...(adapter?.supportsEntryLevelEvaluation()
          ? {
              editingTable: this.props.editingTable,
            }
          : {}),
      };
    },
  },

  /**
   * Produces Ctrl-F search hits for the structured tables contained within a NoteLinkedGroup (e.g. the
   * metadata and ingredients tables of a Mixture Prep group). The group itself is the searchable
   * document item, so the editor never walks into `containedNoteParts` on its own; this method bridges
   * that gap by delegating to each contained table's search implementation and re-rooting the resulting
   * index paths under the group's cid. The editor slices the search-result tree by the item (group) cid,
   * and `NoteLinkedGroupView` then slices again by each contained table's cid, so the contained tables
   * receive the same shape of search props they would as standalone tables.
   */
  *getSearchResults(
    item: NotePart & {item: UnknownNoteLinkedGroup},
    matcher: TextMatcher,
    searchOverFormulas: boolean,
    computedTables: OutputTableMap | null,
    isCollapsed: boolean
  ): Generator<MediocreSearchResult> {
    // A collapsed group does not render its contained tables, so any hit would be unreachable.
    if (isCollapsed) {
      return;
    }
    const group = item.item;
    const groupAdapter = isKnownNoteLinkedGroupAdapter(group.kind)
      ? getNoteLinkedGroupAdapters()[group.kind]
      : undefined;
    // Contained tables only surface their evaluated cell values through the entry-level `computedTables`
    // map when the group participates in entry-level evaluation. Groups that evaluate in isolation are not
    // represented there, so their cell values can't be matched by the document search pipeline; skip them
    // so we never report unreachable hits.
    if (!groupAdapter?.supportsEntryLevelEvaluation()) {
      return;
    }
    for (const containedNotePart of group.containedNoteParts) {
      if (!isStructuredTableNotePart(containedNotePart)) {
        continue;
      }
      const tableAdapter = getStructuredTableAdapters()[containedNotePart.item.kind];
      // Tables rendered as a form do not render Ctrl-F highlights, so excluding them avoids reporting hits
      // the user can't navigate to.
      if (tableAdapter.shouldShowAsForm(containedNotePart.item)) {
        continue;
      }
      if (!doesUserHavePermissionToViewTable(containedNotePart.item)) {
        continue;
      }
      // Contained tables suppress their own title bar inside the group, so the table name has no
      // rendered target. Drop name hits while keeping column-header and cell hits.
      const isTableNameRendered = !tableAdapter.shouldSuppressTitleBar(containedNotePart.item);
      const containedResults = STRUCTURED_TABLE_PLUGIN.getSearchResults?.(
        containedNotePart,
        matcher,
        searchOverFormulas,
        computedTables,
        false /* isCollapsed - group collapse is handled above; contained tables are never independently collapsed */
      );
      for (const result of containedResults ?? []) {
        if (!isTableNameRendered && result.index[1] === TABLE_SEARCH_PATHS.NAME) {
          continue;
        }
        yield {...result, index: [item.cid, ...result.index]};
      }
    }
  },

  getContent(item: NotePart & {item: UnknownNoteLinkedGroup}): unknown {
    return item.item;
  },

  itemFromHTML(
    this: NoteEditor,
    $el: JQuery,
    item: NotePart & {item: UnknownNoteLinkedGroup}
  ): NotePart | null {
    const adapter = getNoteLinkedGroupAdapters()[item.item.kind];
    const displayName = adapter.getDisplayName(item.item);
    const allowCrossEntryPaste = supportsCrossEntryPaste(item.item);
    // Block cross-entry paste for all NoteLinkedGroups except image grids and groups whose adapter
    // opts in via supportsCrossEntryPaste (e.g. stoichiometry/reaction groups). For both, any
    // contained-file server ids are smuggled through the clipboard (see itemToHTML below) so the
    // per-kind paste converter can re-register them in the destination entry.
    // NOTE: this loosens the ENTRY boundary only. The cross-TENANT guard is a separate mechanism
    // in Editor.itemFromHTML (data-origin vs getLocationOrigin()) and stays enforced because this
    // plugin never sets canPasteFromOtherTenant.
    if (
      !allowCrossEntryPaste &&
      $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID) !== this.props.fileId
    ) {
      notifications.warn(`Cannot paste "${displayName}" item between entries.`);
      return null;
    }
    if (
      !adapter.supportsCopyPaste() &&
      getNoteLinkedObjectsOfTypes(this.props.note, [NoteLinkedObjectType.NOTE_LINKED_GROUP]).some(
        (group) => (group as NoteLinkedGroup<unknown>).provenanceUuid === item.item.provenanceUuid
      )
    ) {
      notifications.warn(`Cannot create more than one copy of the same "${displayName}" item.`);
      return null;
    }

    // For grids, read the {containedFileCid -> serverId} map embedded on copy and stash it as a
    // transient field for convertNotePartGridForPaste to consume. Needed because contained-file
    // server ids live only in the source entry's cidIdMap and don't survive a cross-entry paste;
    // the converter has no access to $el. The field is stripped there before the item is persisted.
    if (allowCrossEntryPaste) {
      const containedIdsAttr = $el.attr(NoteConstants.COPY_NOTE_LINKED_GROUP_CONTAINED_IDS);
      if (containedIdsAttr) {
        try {
          stashContainedServerIdsFromClipboard(
            item,
            JSON.parse(containedIdsAttr) as ContainedServerIdsFromClipboard
          );
        } catch (err) {
          if (err instanceof SyntaxError) {
            // Degrade gracefully on malformed clipboard data: getIdFromCid still resolves a same-
            // entry paste, and a missing map only means contained images won't resolve their server
            // ids (no crash). Report it so we notice if some copy path starts emitting bad JSON.
            captureException(err);
          } else {
            throw err;
          }
        }
      }
    }

    return item;
  },

  itemToHTML(this: NoteEditor, $el: JQuery, item: NotePart & {item: UnknownNoteLinkedGroup}): JQuery {
    // TODO(BNCH-14855): Put HTML on clipboard to support external copy and paste
    $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID, this.props.fileId);

    // For cross-entry-capable groups (image grids and stoichiometry/reaction groups), embed a
    // {containedFileCid -> serverId} map so a cross-entry paste can re-register the contained files'
    // server ids in the destination entry. Mirrors the single-image preview pattern
    // (PreviewPluginFactory.itemToHTML's COPY_NOTE_LINKED_OBJECT_DATA_ATTR_ID), generalized to a
    // group's many contained files. A contained file's cid is a server-assigned id that getIdFromCid
    // only resolves in the source entry.
    if (supportsCrossEntryPaste(item.item)) {
      const containedServerIds = collectContainedServerIds(item.item, (containedFileCid) =>
        this.props.getIdFromCid(containedFileCid)
      );
      $el.attr(NoteConstants.COPY_NOTE_LINKED_GROUP_CONTAINED_IDS, JSON.stringify(containedServerIds));
    }

    return $el;
  },

  // Sub-template insertion prepares fetched note parts for the store, which records contained files'
  // server ids under freshly generated cids that the destination entry has never seen. Embedding them
  // here hands the same map to the group's paste converter that a cross-entry paste delivers through
  // the clipboard.
  stashContainedServerIdsForPaste(item: NotePart & {item: UnknownNoteLinkedGroup}, cidIdMap: CidIdMap): void {
    if (!supportsCrossEntryPaste(item.item)) {
      return;
    }
    stashContainedServerIdsFromClipboard(
      item,
      collectContainedServerIds(item.item, (containedFileCid) => cidIdMap[containedFileCid])
    );
  },

  // For NoteLinkedGroups, we either allow full group replacement (e.g. from copy-pasting a new group, or from undo/redo),
  // or partial updates to the group's contained note parts (e.g. from performTableOp).
  // In the former case, we replace the entire item with the new group object. In the latter case, we merge the
  // provided immutability-helper spec into the existing item to update just the relevant contained note parts.
  setContent(
    props: {item: NoteLinkedGroup<unknown>} | {item: {containedNoteParts: {[i: number]: Spec<NotePart>}}}
  ): Spec<NotePart> {
    // Full group replacement: props.item is a complete NoteLinkedGroup object.
    // note: we can't use isNoteLinkedGroup here because in the case of partial updates, props.item is not a complete
    // NotePartItem object, so instead we just check that props.item has the properties of a NoteLinkedGroup directly
    const isNoteLinkedGroup =
      'type' in props.item && props.item.type === NoteLinkedObjectType.NOTE_LINKED_GROUP;
    if (isNoteLinkedGroup) {
      return {
        item: {
          $apply(existingNoteLinkedObject: NoteLinkedObject): NoteLinkedObject {
            return props.item as NoteLinkedObject;
          },
        },
      };
    }
    // Partial update: props.item contains an immutability-helper spec (e.g.
    // {containedNoteParts: {0: Spec, 1: Spec}} from performTableOp updating
    // inner tables after formula shifting). Merge into the existing group.
    return {item: props.item} as Spec<NotePart>;
  },

  getReasonForDisallowedAction(
    this: NoteEditor,
    item: NotePart & {item: UnknownNoteLinkedGroup},
    action: MediocreItemAction
  ): string | null {
    if (action !== MediocreItemAction.DELETE) {
      return null;
    }
    const adapter = getNoteLinkedGroupAdapters()[item.item.kind];
    const allowDelete = adapter.allowDeleteGroup(item.item);
    return allowDelete.canDelete ? null : (allowDelete.reason ?? null);
  },
};
